import { CircularProgress, Box, Typography } from '@mui/material';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { InfoOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import Legend from '../../shared/Dashboard/legend';

const LayoutComponent = (props) => {
  const {
    svgMap, loader, blocks, overAllSVG, svgProgressBlockWise, activityId, list,
    selectBlock = false, setSelectPart, isSelect, type = "CP", selectAll = false,
    selectPart, onBlockClick, editMode = false
  } = props;

  const wrapperRef = useRef(null);
  const clickTimeoutRef = useRef(null);
  const observerRef = useRef(null);
  const eventHandlersRef = useRef(new Map());
  const constructionProgressRef = useRef({});
  const selectBlockRef = useRef(selectBlock);
  const blocksRef = useRef(blocks);
  const progressUpdatedRef = useRef({});
  const blockTextMappingRef = useRef(new Map());
  const navigate = useNavigate();
  const [svgReady, setSvgReady] = useState(false);

  useEffect(() => { blocksRef.current = blocks; }, [blocks]);
  useEffect(() => { selectBlockRef.current = selectBlock; }, [selectBlock]);

  const getTransformPosition = (transformString) => {
    if (!transformString) return null;

    const matrixMatch = transformString.match(/matrix\(([^)]+)\)/);
    if (matrixMatch) {
      const values = matrixMatch[1].split(/[,\s]+/).map(Number);
      if (values.length >= 6) return { x: values[4], y: values[5] };
    }

    const translateMatch = transformString.match(/translate\(([^)]+)\)/);
    if (translateMatch) {
      const values = translateMatch[1].split(/[,\s]+/).map(Number);
      return { x: values[0] || 0, y: values[1] || 0 };
    }

    return null;
  };

  const createBlockTextMapping = useCallback(() => {
    const mapping = new Map();
    if (!blocksRef.current?.length) return mapping;

    const svgElement = document.querySelector('#dashboard-svg svg');
    if (!svgElement) return mapping;

    blocksRef.current.forEach(block => {
      const blockElement = document.getElementById(block.block_svg_id);
      if (!blockElement) return;

      try {
        const blockIdMatch = block.block_svg_id.match(/Block-(\d+)/i) || block.block_svg_id.match(/MVPS(\d+)/i);
        if (blockIdMatch) {
          const blockNumber = blockIdMatch[1];

          const textElements = Array.from(svgElement.querySelectorAll('text'));
          let blockNameElement = null;
          let bestPercentageElement = null;
          let minDistance = Infinity;

          textElements.forEach(textEl => {
            const fullText = textEl.textContent || '';
            if (fullText.trim() === `Block-${blockNumber}`) {
              blockNameElement = textEl;
            }
          });

          if (blockNameElement) {
            const blockNameIndex = textElements.indexOf(blockNameElement);
            if (blockNameIndex !== -1 && blockNameIndex + 1 < textElements.length) {
              const nextTextElement = textElements[blockNameIndex + 1];
              const nextTextContent = nextTextElement.textContent || '';
              if (nextTextContent.includes('%') || /\d+\s*%/.test(nextTextContent)) {
                bestPercentageElement = nextTextElement;
              }
            }
          }

          if (!bestPercentageElement) {
            textElements.forEach(textEl => {
              const textContent = textEl.textContent || '';
              const tspanElements = textEl.querySelectorAll('tspan');

              let hasPercentage = textContent.includes('%') || /\d+\s*%/.test(textContent);

              if (!hasPercentage && tspanElements.length > 0) {
                tspanElements.forEach(tspan => {
                  const tspanContent = tspan.textContent || '';
                  if (tspanContent.includes('%') || /\d+\s*%/.test(tspanContent)) {
                    hasPercentage = true;
                  }
                });
              }

              if (hasPercentage) {
                let distance = Infinity;

                if (blockNameElement) {
                  const nameTransform = blockNameElement.getAttribute('transform');
                  const percentageTransform = textEl.getAttribute('transform');

                  const namePos = getTransformPosition(nameTransform);
                  const percentagePos = getTransformPosition(percentageTransform);

                  if (namePos && percentagePos) {
                    distance = Math.sqrt(Math.pow(namePos.x - percentagePos.x, 2) + Math.pow(namePos.y - percentagePos.y, 2));
                  }
                } else {
                  try {
                    const blockBBox = blockElement.getBBox();
                    const blockCenterX = blockBBox.x + blockBBox.width / 2;
                    const blockCenterY = blockBBox.y + blockBBox.height / 2;

                    const transformPos = getTransformPosition(textEl.getAttribute('transform'));
                    let textCenterX, textCenterY;

                    if (transformPos) {
                      textCenterX = transformPos.x;
                      textCenterY = transformPos.y;
                    } else {
                      const textBBox = textEl.getBBox();
                      textCenterX = textBBox.x + textBBox.width / 2;
                      textCenterY = textBBox.y + textBBox.height / 2;
                    }

                    distance = Math.sqrt(Math.pow(blockCenterX - textCenterX, 2) + Math.pow(blockCenterY - textCenterY, 2));
                  } catch (e) {
                    distance = Infinity;
                  }
                }

                if (distance < 50 && distance < minDistance) {
                  minDistance = distance;
                  bestPercentageElement = textEl;
                }
              }
            });
          }

          if (bestPercentageElement) {
            mapping.set(block.id, bestPercentageElement);
          }
        }
      } catch (e) {
        console.error(e);
      }
    });

    return mapping;
  }, []);

  const handleDblClick = useCallback((block) => {
    if (selectBlockRef.current || onBlockClick) return;
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }
    navigate(`${block?.id}`);
  }, [navigate, onBlockClick]);

  const handleBlockClick = useCallback((block) => {
    if (onBlockClick) {
      onBlockClick(block);
      return;
    }
    if (!isSelect) return;

    const blockElement = document.getElementById(block.block_svg_id);
    if (!blockElement) return;

    setSelectPart((prevSelected) => {
      const isAlreadySelected = prevSelected.includes(block.id);
      if (isAlreadySelected) {
        const progress = constructionProgressRef.current[block.id] || 0;
        const color = progress === 0 ? "#FFFFFF" : progress === 100 ? "#B4F8D7" : "#FCDDD9";
        blockElement.style.fill = color;
        return prevSelected.filter((id) => id !== block.id);
      } else {
        if (type === "QC") {
          prevSelected.forEach((selectedId) => {
            const selectedBlock = blocksRef.current.find(b => b.id === selectedId);
            if (selectedBlock) {
              const selectedElement = document.getElementById(selectedBlock.block_svg_id);
              if (selectedElement) {
                const selectedProgress = constructionProgressRef.current[selectedId] || 0;
                const selectedColor = selectedProgress === 0 ? "#FFFFFF" : selectedProgress === 100 ? "#B4F8D7" : "#FCDDD9";
                selectedElement.style.fill = selectedColor;
              }
            }
          });
          blockElement.style.fill = "#FFFF00";
          return [block.id];
        } else {
          blockElement.style.fill = "#FFFF00";
          return [...prevSelected, block.id];
        }
      }
    });
  }, [isSelect, setSelectPart, type, onBlockClick]);

  const updateTextElement = (textElement, newText) => {
    if (!textElement) return false;

    try {
      const tspanElements = textElement.querySelectorAll('tspan');

      if (tspanElements.length > 0) {
        let updated = false;
        tspanElements.forEach(tspan => {
          const content = tspan.textContent || '';
          if (content.includes('%') || /\d+\s*%/.test(content)) {
            tspan.textContent = newText;
            updated = true;
          }
        });
        if (updated) return true;
      }

      const currentContent = textElement.textContent || '';
      if (currentContent.includes('%') || /\d+\s*%/.test(currentContent)) {
        textElement.textContent = newText;
        return true;
      }

      const childNodes = Array.from(textElement.childNodes);
      for (let node of childNodes) {
        if (node.nodeType === Node.TEXT_NODE) {
          const content = node.textContent || '';
          if (content.includes('%') || /\d+\s*%/.test(content)) {
            node.textContent = newText;
            return true;
          }
        }
      }

      return false;
    } catch (e) {
      console.error(e);
      return false;
    }
  };

  const updateProgress = useCallback((progress, id, tempId, forceUpdate = false) => {
    const applyUpdate = () => {
      const blockElement = document.getElementById(id);
      if (!blockElement) {
        setTimeout(applyUpdate, 100);
        return;
      }

      if (selectAll) {
        blockElement.style.fill = "#FFFF00";
      } else if (selectBlockRef.current && blockElement.style.fill !== "rgb(255, 255, 0)") {
        blockElement.style.fill = "#FFFFFF";
      } else if (!selectBlockRef.current) {
        const color = progress === 0 ? "#FFFFFF" : progress === 100 ? "#B4F8D7" : "#FCDDD9";
        blockElement.style.fill = color;
      }

      let showProgress = type === "CP";

      if (true) {
        const correspondingBlock = blocksRef.current?.find(block => block.block_svg_id === id);
        let percentageTextElement = null;

        if (correspondingBlock && blockTextMappingRef.current.has(correspondingBlock.id)) {
          percentageTextElement = blockTextMappingRef.current.get(correspondingBlock.id);
        }

        if (!percentageTextElement) {
          const blockIdMatch = id.match(/Block-(\d+)|MVPS(\d+)/i);
          if (blockIdMatch) {
            const blockNumber = blockIdMatch[1] || blockIdMatch[2];
            percentageTextElement = document.getElementById(`block-percentage-Block-${blockNumber}`);
          }
        }

        if (!percentageTextElement && blockElement) {
          try {
            const blockBBox = blockElement.getBBox();
            const blockCenterX = blockBBox.x + blockBBox.width / 2;
            const blockCenterY = blockBBox.y + blockBBox.height / 2;
            const svgElement = blockElement.closest('svg');

            if (svgElement) {
              const textElements = svgElement.querySelectorAll('text');
              let closestTextElement = null;
              let minDistance = Infinity;

              textElements.forEach(textEl => {
                try {
                  let hasPercentage = false;
                  const textContent = textEl.textContent || '';
                  const tspanElements = textEl.querySelectorAll('tspan');

                  if (textContent.includes('%') || /\d+\s*%/.test(textContent) || textContent.includes('100')) {
                    hasPercentage = true;
                  } else if (tspanElements.length > 0) {
                    tspanElements.forEach(tspan => {
                      const tspanContent = tspan.textContent || '';
                      if (tspanContent.includes('%') || /\d+\s*%/.test(tspanContent) || tspanContent.includes('100')) {
                        hasPercentage = true;
                      }
                    });
                  }

                  if (hasPercentage) {
                    const transformPos = getTransformPosition(textEl.getAttribute('transform'));
                    let textCenterX, textCenterY;

                    if (transformPos) {
                      textCenterX = transformPos.x;
                      textCenterY = transformPos.y;
                    } else {
                      const textBBox = textEl.getBBox();
                      textCenterX = textBBox.x + textBBox.width / 2;
                      textCenterY = textBBox.y + textBBox.height / 2;
                    }

                    const distance = Math.sqrt(Math.pow(blockCenterX - textCenterX, 2) + Math.pow(blockCenterY - textCenterY, 2));
                    if (distance < 150 && distance < minDistance) {
                      minDistance = distance;
                      closestTextElement = textEl;
                    }
                  }
                } catch (e) {
                  console.error(e);
                }
              });

              if (closestTextElement) {
                percentageTextElement = closestTextElement;
              }
            }
          } catch (e) {
            console.error(e);
          }
        }

        if (percentageTextElement) {
          let newText = '';
          if (showProgress) {
            newText = progress === 0 ? '0 %' : `${Math.round(progress)} %`;
          } else {
            newText = '';
          }
          const updated = updateTextElement(percentageTextElement, newText);
          if (updated) {
            percentageTextElement.setAttribute('data-updated', Date.now().toString());
          }
        }
      }
    };

    applyUpdate();
  }, [selectAll, type]);

  useEffect(() => {
    progressUpdatedRef.current = {};

    if (activityId && activityId !== "All" && svgProgressBlockWise && Array.isArray(svgProgressBlockWise)) {
      let newConstructionProgress = {};
      svgProgressBlockWise.forEach((item) => {
        newConstructionProgress[item.block_id] = Math.round(item.progress || 0);
      });
      constructionProgressRef.current = newConstructionProgress;

      if (svgReady && blocksRef.current?.length > 0) {
        const applyProgressUpdates = (attempt = 0) => {
          blocksRef.current.forEach((block) => {
            if (newConstructionProgress.hasOwnProperty(block.id)) {
              const progress = newConstructionProgress[block.id];
              updateProgress(progress, block.block_svg_id, block.progress_svg_id, true);
            }
          });
          if (attempt < 2) setTimeout(() => applyProgressUpdates(attempt + 1), 200);
        };
        setTimeout(() => applyProgressUpdates(), 100);
      }
      return;
    }

    if (overAllSVG && list && (!activityId || activityId === "All")) {
      let newConstructionProgress = {};
      Object.keys(overAllSVG).forEach((blockId) => {
        let subList = [];
        let subWeight = [];
        overAllSVG[blockId]?.forEach((item) => {
          if (list?.construction?.[item?.activity_id]?.['parent_activity_id']) {
            subWeight.push(list?.construction[item?.activity_id]?.weight || 0);
            subList.push((list?.construction[item?.activity_id]?.weight || 0) * (item?.progress || 0));
          }
        });
        const totalWeight = subWeight.reduce((a, b) => a + b, 0);
        const totalProgress = subList.reduce((a, b) => a + b, 0);
        newConstructionProgress[blockId] = totalWeight > 0 ? Math.round(totalProgress / totalWeight) : 0;
      });
      constructionProgressRef.current = newConstructionProgress;

      if (svgReady && blocksRef.current?.length > 0) {
        const applyOverallUpdates = (attempt = 0) => {
          blocksRef.current.forEach((block) => {
            if (newConstructionProgress.hasOwnProperty(block.id)) {
              const progress = newConstructionProgress[block.id];
              updateProgress(progress, block.block_svg_id, block.progress_svg_id, true);
            }
          });
          if (attempt < 2) setTimeout(() => applyOverallUpdates(attempt + 1), 200);
        };
        setTimeout(() => applyOverallUpdates(), 100);
      }
    } else {
      constructionProgressRef.current = {};
    }
  }, [overAllSVG, list, activityId, svgProgressBlockWise, svgReady, updateProgress]);

  const addEventListeners = useCallback(() => {
    if (!wrapperRef.current || !blocksRef.current?.length) return;

    requestAnimationFrame(() => {
      let allElementsFound = true;
      blocksRef.current.forEach((block) => {
        const blockElement = document.getElementById(block.block_svg_id);
        if (!blockElement) {
          allElementsFound = false;
          return;
        }

        const clickHandler = () => handleBlockClick(block);
        const dblClickHandler = () => handleDblClick(block);

        if (eventHandlersRef.current.has(block.block_svg_id)) {
          const existingHandlers = eventHandlersRef.current.get(block.block_svg_id);
          blockElement.removeEventListener("click", existingHandlers.click);
          blockElement.removeEventListener("dblclick", existingHandlers.dblclick);
        }

        eventHandlersRef.current.set(block.block_svg_id, { click: clickHandler, dblclick: dblClickHandler });
        blockElement.addEventListener("click", clickHandler);
        blockElement.addEventListener("dblclick", dblClickHandler);
      });

      if (allElementsFound) {
        setSvgReady(true);
      } else {
        setTimeout(addEventListeners, 100);
      }
    });
  }, [handleBlockClick, handleDblClick]);

  useEffect(() => {
    if (!blocksRef.current?.length || !selectPart || selectPart.length !== 0 || selectAll) return;
    blocksRef.current.forEach((block) => {
      const blockElement = document.getElementById(block.block_svg_id);
      if (!blockElement) return;
      const progress = constructionProgressRef.current[block.id] || 0;
      const color = progress === 0 ? "#FFFFFF" : progress === 100 ? "#B4F8D7" : "#FCDDD9";
      blockElement.style.fill = color;
    });
  }, [selectPart, selectAll]);

  useEffect(() => {
    if (svgReady && blocksRef.current?.length > 0) {
      const timer = setTimeout(() => {
        blockTextMappingRef.current = createBlockTextMapping();
        blocksRef.current.forEach((block) => {
          const progress = constructionProgressRef.current[block?.id] || 0;
          updateProgress(progress, block.block_svg_id, block.progress_svg_id, true);
        });
        setTimeout(() => {
          blocksRef.current.forEach((block) => {
            const progress = constructionProgressRef.current[block?.id] || 0;
            updateProgress(progress, block.block_svg_id, block.progress_svg_id, true);
          });
        }, 300);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [svgReady, createBlockTextMapping, updateProgress, activityId]);

  useEffect(() => {
    if (!svgMap || loader) return;

    observerRef.current = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          requestAnimationFrame(() => addEventListeners());
          break;
        }
      }
    });

    if (wrapperRef.current) {
      observerRef.current.observe(wrapperRef.current, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['id']
      });
    }

    const attemptListeners = () => {
      addEventListeners();
      const retryTimes = [100, 300, 500, 1000];
      retryTimes.forEach((delay, index) => {
        setTimeout(() => {
          if (!svgReady) addEventListeners();
        }, delay);
      });
    };

    setTimeout(attemptListeners, 100);

    return () => {
      observerRef.current?.disconnect();
      const currentHandlers = new Map(eventHandlersRef.current);
      currentHandlers.forEach(({ click, dblclick }, id) => {
        const blockElement = document.getElementById(id);
        if (blockElement) {
          blockElement.removeEventListener("click", click);
          blockElement.removeEventListener("dblclick", dblclick);
        }
      });
      // eslint-disable-next-line
      eventHandlersRef?.current.clear();
    };
  }, [addEventListeners, svgMap, loader, svgReady]);

  useEffect(() => {
    const currentBlocks = blocksRef.current;
    if (!currentBlocks?.length) return;

    if (selectBlock) {
      currentBlocks.forEach((block) => {
        const blockElement = document.getElementById(block.block_svg_id);
        if (blockElement && !selectAll) blockElement.style.fill = "#FFFFFF";
      });
    } else {
      if (!selectAll) setSelectPart([]);
      currentBlocks.forEach((block) => {
        if (!selectAll) {
          updateProgress(constructionProgressRef.current[block?.id] || 0, block?.block_svg_id, block?.progress_svg_id);
        }
      });
    }
  }, [selectBlock, setSelectPart, updateProgress, selectAll]);

  useEffect(() => {
    if (!blocksRef.current?.length || Object.keys(constructionProgressRef.current).length === 0) return;
    const currentBlocks = blocksRef.current;
    if (svgReady) {
      setTimeout(() => {
        currentBlocks.forEach((block) => {
          const progress = constructionProgressRef.current[block.id];
          if (progress !== undefined && !progressUpdatedRef.current[block.block_svg_id]) {
            updateProgress(progress, block.block_svg_id, block.progress_svg_id, true);
            progressUpdatedRef.current[block.block_svg_id] = true;
          }
        });
      }, 150);
    }
  }, [updateProgress, svgReady]);

  useEffect(() => {
    if (!blocksRef.current?.length || !selectAll) return;
    blocksRef.current.forEach((block) => {
      const blockElement = document.getElementById(block.block_svg_id);
      if (blockElement) blockElement.style.fill = "#FFFF00";
    });
    const allBlockIds = blocksRef.current.map(block => block.id);
    setSelectPart(allBlockIds);
  }, [selectAll, setSelectPart]);

  const adjustSvg = useCallback((svg) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = svg;
    const svgElement = tempDiv.querySelector("svg");
    if (svgElement) {
      svgElement.setAttribute("width", "100%");
      svgElement.setAttribute("height", "100%");
      if (!svgElement.hasAttribute("viewBox")) {
        const bbox = svgElement.getBBox();
        svgElement.setAttribute("viewBox", `${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`);
      }
      svgElement.style.backgroundColor = "#FFFFFF";
      const styleTag = document.createElement("style");
      styleTag.textContent = `path, polygon, rect { stroke-width: 1px; }`;
      svgElement.appendChild(styleTag);
    }
    return tempDiv.innerHTML;
  }, []);

  const adjustedSvgMap = useMemo(() => svgMap ? adjustSvg(svgMap) : '', [svgMap, adjustSvg]);

  const getInfoMessage = () => {
    if (type === "ConstructionProgressSequence") {
      return editMode ? "Click on any block to add to sequence" : "Click on Edit Sequence to edit sequence";
    }
    return "Click on any block to select";
  };

  const memoizedContent = useMemo(() => (
    <Box sx={{ display: "flex", cursor: "grab", justifyContent: "center", alignItems: "center" }}
      style={{ width: '100%', height: `calc(100vh - ${type === "Scope Laydown" ? "230px" : "190px"})`, overflow: 'hidden' }}>
      {loader ? (
        <CircularProgress />
      ) : (
        <TransformWrapper centerZoomedOut minScale={0.2} centerOnInit limitToBounds doubleClick={{ disabled: true }}
          onInit={() => setTimeout(() => addEventListeners(), 200)}>
          <TransformComponent wrapperStyle={{ height: "100%", width: "100%" }}>
            <div id="dashboard-svg" ref={wrapperRef} className="wrapper-component"
              style={{ width: "100%", height: "100%", cursor: "pointer", position: "relative" }}
              dangerouslySetInnerHTML={{ __html: adjustedSvgMap }}
              onLoad={() => setTimeout(() => addEventListeners(), 200)} />
          </TransformComponent>
        </TransformWrapper>
      )}

      {(isSelect || type === "ConstructionProgressSequence") &&
        <Box sx={{ position: "absolute", bottom: type === "logs and checklists" ? 50 : 55, left: "28%", flexDirection: "row", alignItems: "center", display: "flex", gap: 1 }}>
          <InfoOutlined  />
          <Typography fontSize={14} color="#9ca3af">{getInfoMessage()}</Typography>
        </Box>}

      {type === "CP" && (
        <Box sx={{
          position: "absolute",
          bottom: 30,
          left: 30
        }}>
          <Legend layoutType="SVG" />
        </Box>
      )}

      {type === "CP" && type !== "Scope Laydown" && (
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 0.5, position: "absolute", bottom: 15, left: "25%" }}>
          <InfoOutlined />
          <Typography ml={1} fontSize={14} fontWeight={500} color="#9ca3af">
            Double click on any block to dive in
          </Typography>
        </Box>
      )}
    </Box>
    // eslint-disable-next-line 
  ), [loader, adjustedSvgMap, addEventListeners, type, isSelect, editMode]);

  useEffect(() => {
    if (!overAllSVG || !blocksRef.current?.length) return;
    const applyColors = () => {
      let allElementsFound = true;
      blocksRef.current.forEach((block) => {
        const blockElement = document.getElementById(block.block_svg_id);
        if (!blockElement) {
          allElementsFound = false;
          return;
        }
        if (selectAll) {
          blockElement.style.fill = "#FFFF00";
        } else {
          const blockData = overAllSVG[block.id];
          if (blockData?.[0]?.color) {
            blockElement.style.fill = blockData[0].color;
          } else {
            const progress = constructionProgressRef.current[block.id] || 0;
            const color = progress === 0 ? "#FFFFFF" : progress === 100 ? "#B4F8D7" : "#FCDDD9";
            blockElement.style.fill = color;
          }
        }
      });
      if (!allElementsFound) setTimeout(applyColors, 100);
    };

    if (svgReady) {
      applyColors();
    } else {
      const checkSvgReady = () => {
        if (svgReady) applyColors();
        else setTimeout(checkSvgReady, 100);
      };
      checkSvgReady();
    }
  }, [overAllSVG, blocks, svgReady, selectAll]);

  return memoizedContent;
};

export default LayoutComponent;